import { useMutation } from '@tanstack/react-query';

import container from '../../../../container';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';
import { ClaimFileType } from '../types';
import { createUploaderFileFromClaimFile } from '../../../claims-center/submit-claims/createClaimContext';

type BaseFileImportOptions = {
  shopId: number;
  onChange: (data: Record<string, unknown>) => void;
  extractInvoiceData?: (fileId: number) => void;
};

type InvoiceFileImportOptions = BaseFileImportOptions & {
  fileType: ClaimFileType.INVOICE_DOCUMENT;
  trackingCode: string | undefined;
};

type DORFileImportOptions = BaseFileImportOptions & {
  fileType: ClaimFileType.DECLARATION_OF_RECIPIENT;
  isDORRequired: boolean;
  shipmentId: number | undefined | null;
};

export type UseFileImportOptions = InvoiceFileImportOptions | DORFileImportOptions;

export type ImportFileState = 'not_enabled' | 'searching' | 'error' | 'found' | 'not_found';

export function useFileImport(options: UseFileImportOptions): [() => void, ImportFileState] {
  const { shopId, onChange } = options;
  const { isInvoiceImportEnabled, isDORSigningLinkEnabled } = useClaimsShopSettings();

  const isInvoiceImport = options.fileType === ClaimFileType.INVOICE_DOCUMENT;
  const isDORImport = options.fileType === ClaimFileType.DECLARATION_OF_RECIPIENT;

  const fileKey = isInvoiceImport ? 'invoiceDocuments' : 'declarationOfRecipient';
  const fetch = isInvoiceImport
    ? () => {
        if (!options.trackingCode) {
          throw new Error('Tracking code is required for invoice import');
        }

        return container.touchpointsApi.postClaimsImportInvoice(shopId, { trackingCode: options.trackingCode });
      }
    : () => {
        if (!options.shipmentId) {
          throw new Error('Shipment ID is required for DOR import');
        }

        return container.touchpointsApi.postClaimsImportDOR(shopId, { shipmentId: options.shipmentId });
      };

  const { mutate, isLoading, isError, data } = useMutation(fetch, {
    mutationKey: ['postClaimsImport', shopId, options.fileType],
    onSuccess: (data) => {
      const { file: claimFile } = data;

      if (claimFile) {
        onChange({ [fileKey]: [createUploaderFileFromClaimFile(claimFile)] });
        if (isInvoiceImport) {
          options.extractInvoiceData?.(claimFile.id);
        }
      }
    },
  });

  const featureFlag = isInvoiceImport ? isInvoiceImportEnabled : isDORSigningLinkEnabled;

  if (!featureFlag) {
    return [() => {}, 'not_enabled'];
  }

  if (isDORImport && !options.isDORRequired) {
    return [() => {}, 'not_enabled'];
  }

  if (isDORImport && !options.shipmentId) {
    return [() => {}, 'not_enabled'];
  }

  if (isLoading) {
    return [mutate, 'searching'];
  }

  if (isError) {
    return [mutate, 'error'];
  }

  return [mutate, data?.status === 'found' ? 'found' : 'not_found'];
}
